import { Component, OnInit, inject, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentService } from '@core/services/payment.service';
import { LoggingService } from '@core/services/logging.service';

/** The page a company's first plan purchase continues to (#943). */
export const FIRST_SCAN_ROUTE = '/dashboard/first-scan';

/** True when the checkout's success URL asked for the first-scan page. */
function firstScanRequested(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('next') === 'first-scan';
}

/**
 * CheckoutSuccessComponent
 *
 * Landing page after a successful Stripe checkout redirect.
 * Stripe appends ?session_id=... to the successUrl; this component:
 *   1. Calls PaymentService.checkForPaymentSuccess() to verify the session
 *      and log the upgrade event.
 *   2. Shows a confirmation state while processing.
 *   3. Redirects after a brief delay so the user sees feedback: to "Run your
 *      first scan" for a first plan (`&next=first-scan`, #943), otherwise to
 *      /dashboard/.
 *
 * Route: /register/checkout-success
 * successUrl: <origin>/register/checkout-success?session_id={CHECKOUT_SESSION_ID}
 */
@Component({
  selector: 'app-checkout-success',
  templateUrl: './checkout-success.component.html',
  styleUrls: ['./checkout-success.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CheckoutSuccessComponent implements OnInit {
  private router = inject(Router);
  private paymentService = inject(PaymentService);
  private loggingService = inject(LoggingService);
  private ngZone = inject(NgZone);

  /** Current processing state */
  state: 'processing' | 'success' | 'error' = 'processing';

  ngOnInit(): void {
    this.processCheckout();
  }

  /**
   * Verifies the Stripe session from the URL and updates state accordingly.
   * Redirects to dashboard after 3 seconds on success, 5 seconds on error.
   */
  private async processCheckout(): Promise<void> {
    console.warn('[CHECKOUT-DEBUG] processCheckout: START — full URL =', window.location.href);
    console.warn('[CHECKOUT-DEBUG] processCheckout: URL search params =', window.location.search);
    try {
      console.warn('[CHECKOUT-DEBUG] processCheckout: calling paymentService.checkForPaymentSuccess()');
      const success = await this.paymentService.checkForPaymentSuccess();
      console.warn('[CHECKOUT-DEBUG] processCheckout: checkForPaymentSuccess() returned', success);
      this.state = success ? 'success' : 'error';
      console.warn('[CHECKOUT-DEBUG] processCheckout: state set to', this.state);
    } catch (error) {
      console.warn('[CHECKOUT-DEBUG] processCheckout: caught unexpected error', error);
      this.loggingService.logError('checkout-success: unexpected error', error);
      this.state = 'error';
    }

    const delay = this.state === 'success' ? 3000 : 5000;
    // A first plan goes on to "Run your first scan" (#943). That page is open
    // before the webhook lands, so it is also right when verification lagged.
    const destination = firstScanRequested() ? FIRST_SCAN_ROUTE : '/dashboard/';
    console.warn(`[CHECKOUT-DEBUG] processCheckout: will redirect to /dashboard/ in ${delay}ms (state: ${this.state})`);
    // Run outside Angular zone so the setTimeout does not block application
    // stability and trigger NG0506 hydration timeout warnings.
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          console.warn('[CHECKOUT-DEBUG] processCheckout: navigating to', destination);
          this.router.navigate([destination]);
        });
      }, delay);
    });
  }
}
